import React, { useState, useEffect } from "react";
import {
    Button,
    Card,
    Col,
    DatePicker,
    Divider,
    Drawer,
    Input,
    Row,
    Select,
    Space,
    Tag,
    message,
} from "antd";
import {
    SearchOutlined,
    EyeOutlined,
    DownloadOutlined,
    FileTextOutlined,
    CloseCircleOutlined,
    ExclamationCircleOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { historyAPIWithErrorHandling } from "../../../api/histories/histories";
import BrandedPageLayout from "../../../components/Dashboard/BrandedPageLayout";
import ListTableCard from "../../../components/common/ListTableCard";
import { actionColumnDef } from "../../../components/common/RowActions";
import ListToolbar, {
    selectVisibleColumns,
    useListToolbar,
} from "../../../components/common/ListToolbar";
import { neutral, spacing } from "../../../theme/tokens";

const { RangePicker } = DatePicker;

const DEFAULT_VISIBLE_FILTERS = [];
const DEFAULT_VISIBLE_COLUMNS = [
    "total_rows", "success_rows", "fail_rows", "type", "status", "created_at",
];

export default function ImportHistoryTable() {
    // State management
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState([]);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 15,
        total: 0,
    });
    const [searchText, setSearchText] = useState("");
    const [dateRange, setDateRange] = useState([null, null]);
    const [selectedType, setSelectedType] = useState("");
    const [errorModalVisible, setErrorModalVisible] = useState(false);
    const [selectedRecord, setSelectedRecord] = useState(null);

    const {
        visibleFilters, setVisibleFilters,
        visibleColumns, setVisibleColumns,
    } = useListToolbar({
        defaultVisibleFilters: DEFAULT_VISIBLE_FILTERS,
        defaultVisibleColumns: DEFAULT_VISIBLE_COLUMNS,
    });

    // Import type options
    const paymentMethods = [
        { value: "", label: "Tất cả" },
        { value: "customer", label: "Khách hàng" },
        { value: "collection", label: "Đợt thu" },
    ];

    // Load import histories
    const loadData = async (params = {}) => {
        try {
            setLoading(true);

            // Prepare date params
            const dateParams = {};
            if (dateRange[0] && dateRange[1]) {
                dateParams.start_date = dateRange[0].format("YYYY-MM-DD");
                dateParams.end_date = dateRange[1].format("YYYY-MM-DD");
            }

            const response = await historyAPIWithErrorHandling.getHistories({
                page: pagination.current,
                per_page: pagination.pageSize,
                search: searchText,
                type: selectedType,
                ...dateParams,
                ...params,
            });

            if (response.success) {
                const { data: responseData } = response;
                setData(responseData.data);
                setPagination({
                    ...pagination,
                    current: responseData.current_page,
                    total: responseData.total,
                });
            }
        } catch (error) {
            message.error(error.message);
        } finally {
            setLoading(false);
        }
    };

    // Load data on component mount
    useEffect(() => {
        loadData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Handle search
    const handleSearch = () => {
        setPagination({ ...pagination, current: 1 });
        loadData({ page: 1 });
    };

    // Handle reset
    const handleReset = () => {
        setSearchText("");
        setDateRange([null, null]);
        setSelectedType("");
        setPagination({ ...pagination, current: 1 });
        loadData({
            page: 1,
            search: "",
            type: "",
            start_date: undefined,
            end_date: undefined
        });
    };

    // Handle pagination change
    const handleTableChange = (newPagination) => {
        setPagination(newPagination);
        loadData({
            page: newPagination.current,
            per_page: newPagination.pageSize,
        });
    };

    // Handle view error details
    const handleViewErrors = (record) => {
        setSelectedRecord(record);
        setErrorModalVisible(true);
    };

    // Handle download file
    const handleDownloadFile = (record) => {
        const baseUrl = process.env.REACT_APP_API_URL;
        // Temporary implementation - show message
        message.info(`Tải xuống file: ${record.filename}`);

        // TODO: Implement actual download logic
        const downloadUrl = baseUrl ? `${baseUrl}/api/v1/datamanagement/imports/download/${record.id}` : `/api/v1/datamanagement/imports/download/${record.id}`;
        window.open(downloadUrl, '_blank');
    };

    // ===== Định nghĩa cột =====
    const columnDefs = [
        {
            key: "index", label: "STT", fixed: true,
            column: {
                title: "STT", key: "index", width: 80, fixed: "left", align: "center",
                render: (_, record, index) =>
                    (pagination.current - 1) * pagination.pageSize + index + 1,
            },
        },
        {
            key: "total_rows", label: "Tổng bản ghi",
            column: {
                title: "Tổng bản ghi", dataIndex: "total_rows", key: "total_rows",
                align: "center", width: 200,
                render: (text) => text || "-",
            },
        },
        {
            key: "success_rows", label: "Thành công",
            column: {
                title: "Thành công", dataIndex: "success_rows", key: "success_rows",
                align: "center", width: 200,
                render: (text) => text || "-",
            },
        },
        {
            key: "fail_rows", label: "Thất bại",
            column: {
                title: "Thất bại", dataIndex: "fail_rows", key: "fail_rows",
                align: "center", width: 200,
                render: (text) => text || "-",
            },
        },
        {
            key: "type", label: "Loại",
            column: {
                title: "Loại", dataIndex: "type", key: "type", width: 150,
                render: (type) => {
                    const typeMap = {
                        customer: { text: "Khách hàng", color: "volcano" },
                        collection: { text: "Đợt thu", color: "geekblue" },
                    };
                    const typeInfo = typeMap[type] || { text: type, color: "default" };
                    return <Tag color={typeInfo.color}>{typeInfo.text}</Tag>;
                },
            },
        },
        {
            key: "status", label: "Trạng thái",
            column: {
                title: "Trạng thái", dataIndex: "status", key: "status", width: 150,
                render: (status) => {
                    const statusMap = {
                        completed: { text: "Hoàn thành", color: "success" },
                        processing: { text: "Đang xử lý", color: "processing" },
                        failed: { text: "Thất bại", color: "error" },
                        queued: { text: "Chờ xử lý", color: "warning" },
                    };
                    const statusInfo = statusMap[status] || { text: status, color: "default" };
                    return <Tag color={statusInfo.color}>{statusInfo.text}</Tag>;
                },
            },
        },
        {
            key: "created_at", label: "Ngày thực hiện",
            column: {
                title: "Ngày thực hiện", dataIndex: "created_at", key: "created_at", width: 150,
                render: (created_at) => dayjs(created_at).format("DD/MM/YYYY HH:mm:ss") || "-",
            },
        },
        actionColumnDef(
            (record) => [
                {
                    key: "errors", label: "Xem lỗi", icon: <EyeOutlined />, tone: "warning",
                    hidden: !(record.fail_rows > 0 && record.logs),
                    onClick: () => handleViewErrors(record),
                },
                { key: "download", label: "Tải file", icon: <DownloadOutlined />, tone: "view", onClick: () => handleDownloadFile(record) },
            ],
            { width: 100 }
        ),
    ];

    // ===== Định nghĩa bộ lọc =====
    const filterDefs = [
        {
            key: "search", label: "Tìm kiếm", span: { xs: 24, sm: 12, md: 8 },
            field: (
                <Input
                    placeholder="Nhập để tìm kiếm..."
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    onPressEnter={handleSearch}
                    allowClear
                />
            ),
        },
        {
            key: "type", label: "Loại", span: { xs: 24, sm: 12, md: 8 },
            field: (
                <Select
                    style={{ width: "100%" }}
                    placeholder="Chọn phương thức"
                    value={selectedType}
                    onChange={(value) => setSelectedType(value)}
                    options={paymentMethods}
                    allowClear
                />
            ),
        },
        {
            key: "dateRange", label: "Khoảng ngày", span: { xs: 24, sm: 12, md: 8 },
            field: (
                <RangePicker
                    style={{ width: "100%" }}
                    format="DD/MM/YYYY"
                    placeholder={["Từ ngày", "Đến ngày"]}
                    value={dateRange}
                    onChange={(dates) => setDateRange(dates || [null, null])}
                />
            ),
        },
    ];

    const columns = selectVisibleColumns(columnDefs, visibleColumns);
    const hasActiveFilter = !!(searchText || selectedType || (dateRange && dateRange[0] && dateRange[1]));

    return (
        <BrandedPageLayout
            title="Lịch sử nhập liệu"
            subtitle="Theo dõi kết quả các lần nhập dữ liệu từ file."
            breadcrumbItems={[{ title: "Lịch sử nhập liệu" }]}
        >
            <ListToolbar
                filterDefs={filterDefs}
                visibleFilters={visibleFilters}
                onChangeVisibleFilters={setVisibleFilters}
                columnDefs={columnDefs}
                visibleColumns={visibleColumns}
                onChangeVisibleColumns={setVisibleColumns}
                defaultVisibleColumns={DEFAULT_VISIBLE_COLUMNS}
                hasActiveFilter={hasActiveFilter}
                onClearFilter={handleReset}
                onReload={() => loadData()}
                extra={
                    <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>
                        Tìm kiếm
                    </Button>
                }
            />

            <ListTableCard
                columns={columns}
                dataSource={data}
                loading={loading}
                pagination={{
                    current: pagination.current,
                    pageSize: pagination.pageSize,
                    total: pagination.total,
                }}
                onChange={handleTableChange}
                emptyText="Không có dữ liệu"
            />

            {/* Error Details Drawer */}
            <Drawer
                title={
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <ExclamationCircleOutlined style={{ color: '#E5065C' }} />
                        <span>Chi tiết lỗi import</span>
                    </div>
                }
                placement="right"
                width="min(880px, 100vw)"
                open={errorModalVisible}
                onClose={() => setErrorModalVisible(false)}
                destroyOnClose
                styles={{ body: { background: neutral[50], padding: spacing[6] } }}
                footer={
                    <div style={{ display: "flex", justifyContent: "flex-end", gap: spacing[2] }}>
                        <Button onClick={() => setErrorModalVisible(false)}>
                            Đóng
                        </Button>
                    </div>
                }
            >
                {selectedRecord && selectedRecord.logs && (
                    <div>
                        {/* Header thông tin tổng quan */}
                        <div style={{
                            background: '#F8FAFC',
                            padding: '16px',
                            borderRadius: '8px',
                            marginBottom: '16px'
                        }}>
                            <Row gutter={16}>
                                <Col span={12}>
                                    <div style={{ color: '#64748B', fontSize: '14px' }}>Tên file:</div>
                                    <div style={{ fontWeight: 500, marginTop: '4px' }}>
                                        {selectedRecord.filename}
                                    </div>
                                </Col>
                                <Col span={12}>
                                    <div style={{ color: '#64748B', fontSize: '14px' }}>Tổng số lỗi:</div>
                                    <div style={{ fontWeight: 500, marginTop: '4px', color: '#E5065C' }}>
                                        {selectedRecord.fail_rows} bản ghi
                                    </div>
                                </Col>
                            </Row>
                        </div>

                        <Divider style={{ margin: '16px 0' }} />

                        {/* Danh sách lỗi */}
                        <div style={{ maxHeight: '500px', overflowY: 'auto', paddingRight: '8px' }}>
                            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                                {selectedRecord.logs.map((log, index) => (
                                    <Card
                                        key={index}
                                        size="small"
                                        style={{
                                            borderLeft: '4px solid #E5065C',
                                            boxShadow: '0 1px 2px rgba(0,0,0,0.06)'
                                        }}
                                    >
                                        {/* Header của từng lỗi */}
                                        <div style={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            marginBottom: '12px'
                                        }}>
                                            <Tag color="red">Dòng {log.row_index}</Tag>
                                            <span style={{ fontSize: '12px', color: '#94A3B8' }}>
                                                Lỗi #{index + 1}/{selectedRecord.logs.length}
                                            </span>
                                        </div>

                                        {/* Danh sách lỗi */}
                                        <div style={{ marginBottom: '12px' }}>
                                            <div style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '8px',
                                                marginBottom: '8px'
                                            }}>
                                                <CloseCircleOutlined style={{ color: '#E5065C' }} />
                                                <strong style={{ color: '#E5065C' }}>Lỗi phát hiện:</strong>
                                            </div>
                                            <div style={{
                                                background: '#fff2f0',
                                                padding: '12px',
                                                borderRadius: '4px',
                                                border: '1px solid #FFB8C9'
                                            }}>
                                                <ul style={{
                                                    margin: 0,
                                                    paddingLeft: '20px',
                                                    listStyle: 'none'
                                                }}>
                                                    {log.errors.map((error, errIndex) => (
                                                        <li
                                                            key={errIndex}
                                                            style={{
                                                                fontSize: '14px',
                                                                color: '#E5065C',
                                                                marginBottom: '4px',
                                                                display: 'flex',
                                                                gap: '8px'
                                                            }}
                                                        >
                                                            <span style={{ color: '#FFB8C9' }}>•</span>
                                                            <span>{error}</span>
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        </div>

                                        {/* Dữ liệu */}
                                        <div>
                                            <div style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '8px',
                                                marginBottom: '8px'
                                            }}>
                                                <FileTextOutlined style={{ color: '#64748B' }} />
                                                <strong style={{ color: '#475569' }}>Dữ liệu gốc:</strong>
                                            </div>
                                            <div style={{
                                                background: '#F8FAFC',
                                                padding: '12px',
                                                borderRadius: '4px',
                                                border: '1px solid #CBD5E1'
                                            }}>
                                                <pre style={{
                                                    fontSize: '12px',
                                                    overflowX: 'auto',
                                                    whiteSpace: 'pre-wrap',
                                                    color: '#475569',
                                                    margin: 0,
                                                    fontFamily: 'monospace'
                                                }}>
                                                    {JSON.stringify(log.row_data, null, 2)}
                                                </pre>
                                            </div>
                                        </div>
                                    </Card>
                                ))}
                            </Space>
                        </div>

                        {/* Footer info */}
                        {selectedRecord.logs.length > 5 && (
                            <div style={{
                                marginTop: '16px',
                                textAlign: 'center',
                                fontSize: '14px',
                                color: '#64748B'
                            }}>
                                Hiển thị tất cả {selectedRecord.logs.length} lỗi
                            </div>
                        )}
                    </div>
                )}
            </Drawer>
        </BrandedPageLayout>
    );
}
